export const SECTIONS = [
  {
    icon: '🚀', title: 'Getting Started', color: '#2563eb', bg: '#eff6ff',
    articles: [
      { slug: 'creating-first-campaign',       title: 'Creating your first campaign',              time: '3 min' },
      { slug: 'installing-tracking-script',     title: 'Installing the tracking script',            time: '5 min' },
      { slug: 'connecting-google-ads',          title: 'Connecting your Google Ads account',        time: '4 min' },
      { slug: 'understanding-fraud-report',     title: 'Understanding your first fraud report',     time: '6 min' },
    ],
  },
  {
    icon: '📋', title: 'Installation Guides', color: '#7c3aed', bg: '#f5f3ff',
    articles: [
      { slug: 'wordpress-installation',         title: 'WordPress installation guide',              time: '4 min' },
      { slug: 'shopify-installation',           title: 'Shopify installation guide',                time: '3 min' },
      { slug: 'webflow-installation',           title: 'Webflow installation guide',                time: '3 min' },
      { slug: 'google-tag-manager-setup',       title: 'Google Tag Manager setup',                  time: '5 min' },
      { slug: 'custom-html-installation',       title: 'Custom HTML installation',                  time: '2 min' },
    ],
  },
  {
    icon: '🔗', title: 'Google Ads Integration', color: '#059669', bg: '#f0fdf4',
    articles: [
      { slug: 'finding-customer-id',            title: 'Finding your Google Ads Customer ID',       time: '2 min' },
      { slug: 'how-ip-exclusions-work',         title: 'How IP exclusions work in Google Ads',      time: '5 min' },
      { slug: 'campaign-level-exclusions',      title: 'Setting up Google Ads sync',               time: '4 min' },
      { slug: 'verifying-blocks-working',       title: 'Verifying blocks are working',              time: '3 min' },
    ],
  },
  {
    icon: '📊', title: 'Analytics & Reports', color: '#d97706', bg: '#fffbeb',
    articles: [
      { slug: 'understanding-fraud-score',      title: 'Understanding your fraud score',            time: '5 min' },
      { slug: 'reading-analytics-dashboard',    title: 'Reading the fraud analytics dashboard',     time: '6 min' },
      { slug: 'setting-up-email-reports',       title: 'Setting up email reports',                  time: '3 min' },
      { slug: 'exporting-click-data',           title: 'Exporting your click data',                 time: '2 min' },
    ],
  },
  {
    icon: '⚙️', title: 'Detection Rules', color: '#0891b2', bg: '#ecfeff',
    articles: [
      { slug: 'fraud-detection-signals',        title: 'How fraud detection signals work',          time: '7 min' },
      { slug: 'testing-detection-rules',        title: 'Testing your detection rules',              time: '4 min' },
      { slug: 'custom-detection-rules',         title: 'Creating custom detection rules',           time: '8 min' },
      { slug: 'adjusting-thresholds',           title: 'Adjusting click frequency thresholds',      time: '4 min' },
      { slug: 'country-blocking',               title: 'Blocking clicks by country',                time: '4 min' },
      { slug: 'monitoring-only-mode',           title: 'Using Monitoring Only mode',                time: '3 min' },
      { slug: 'whitelisting-ips',               title: 'Whitelisting trusted IPs',                  time: '3 min' },
    ],
  },
  {
    icon: '💳', title: 'Billing & Account', color: '#dc2626', bg: '#fef2f2',
    articles: [
      { slug: 'changing-plan',                  title: 'Upgrading or downgrading your plan',        time: '2 min' },
      { slug: 'updating-payment',               title: 'Updating your payment method',              time: '2 min' },
      { slug: 'understanding-invoice',          title: 'Understanding your invoice',                time: '3 min' },
      { slug: 'cancelling-subscription',        title: 'Cancelling your subscription',              time: '2 min' },
    ],
  },
];

export const ARTICLES = {

  // ─── GETTING STARTED ───────────────────────────────────────────────────────

  'creating-first-campaign': {
    title: 'Creating your first campaign',
    category: 'Getting Started',
    time: '3 min',
    intro: 'A campaign in ClickSaver maps to one website or landing page you want to protect. Follow these steps to get your first campaign running in under 3 minutes.',
    sections: [
      {
        heading: 'Step 1 — Go to Campaigns',
        content: 'Log in to your ClickSaver dashboard and click Campaigns in the left sidebar. Then click the + New Campaign button in the top right.',
      },
      {
        heading: 'Step 2 — Enter campaign details',
        content: 'Fill in the following fields:',
        steps: [
          'Campaign Name — a label for your own reference (e.g. "Homepage - Brand Campaign")',
          'Domain — the domain of the website you want to protect (e.g. example.com). Do not include https://',
          'Google Ads Customer ID — your 10-digit Google Ads account ID (format: 123-456-7890)',
          'CPC (optional) — your average cost-per-click in your Google Ads campaign. Used to calculate estimated savings.',
        ],
      },
      {
        heading: 'Step 3 — Save and get your script',
        content: 'Click Create Campaign. You will be taken to the campaign detail page where your unique tracking script is displayed. Copy this script — you will need it in the next step.',
      },
      {
        heading: 'Step 4 — Install the script',
        content: 'Paste the tracking script into the <head> section of your website. See the Installation Guides section for platform-specific instructions (WordPress, Shopify, Webflow, etc.).',
      },
      {
        heading: 'Step 5 — Link your Google Ads campaign',
        content: 'Go to Google Ads Sync in the sidebar, select your campaign, and link it to the corresponding Google Ads campaign. This enables ClickSaver to automatically push blocked IPs to your Google Ads exclusion list.',
        note: 'You must connect your Google Ads account in Settings before you can link a campaign.',
      },
    ],
  },

  'installing-tracking-script': {
    title: 'Installing the tracking script',
    category: 'Getting Started',
    time: '5 min',
    intro: 'The ClickSaver tracking script is a lightweight JavaScript snippet (under 2KB) that detects and records every click arriving from your Google Ads campaigns.',
    sections: [
      {
        heading: 'Where to find your script',
        content: 'Go to Campaigns → select your campaign → the script is shown in the Setup tab. Each campaign has a unique script.',
      },
      {
        heading: 'Where to install it',
        content: 'Paste the script inside the <head> tag of every page your Google Ads send traffic to — typically your landing page or homepage. It must load on the page that receives the click, not just the homepage.',
        code: `<!-- ClickSaver Tracking Script -->
<noscript><meta http-equiv="refresh" content="0; url=about:blank"></noscript>
<script>
(function() {
    var tid = 'YOUR_TRACKING_ID';
    var api = 'https://clicksaver.app';
    var did = '';
    try {
        did = localStorage.getItem('_cs_did') || '';
        if (!did) {
            did = 'cs_' + Math.random().toString(36).substr(2,9) + Date.now().toString(36);
            localStorage.setItem('_cs_did', did);
        }
    } catch(e) {}
    function p(n) { return new URLSearchParams(window.location.search).get(n) || ''; }
    var gclid = p('gclid'), fbclid = p('fbclid');
    if (gclid || fbclid) {
        // Behavior tracking
        var scrollDepth = 0, mouseMoved = 0, pageClicks = 0;
        var t0 = Date.now();
        var img = new Image();
        img.src = api + '/api/clicks/pixel/' + tid + '?gclid=' + encodeURIComponent(gclid) + '&did=' + encodeURIComponent(did);
        window.addEventListener('pagehide', function() {
            var duration = Math.round((Date.now() - t0) / 1000);
            navigator.sendBeacon(api + '/api/clicks/session-end/' + tid
                + '?did=' + encodeURIComponent(did) + '&duration=' + duration
                + '&scroll=' + scrollDepth + '&moved=' + mouseMoved + '&clicks=' + pageClicks);
        });
    }
})();
</script>`,
      },
      {
        heading: 'What the script does',
        content: 'The script performs several functions automatically:',
        steps: [
          'Blocks JavaScript-disabled browsers immediately via the <noscript> redirect',
          'Assigns a persistent device ID stored in localStorage to track the same device across sessions',
          'Fires a tracking pixel when a Google Ads click (gclid) is detected in the URL',
          'Records scroll depth, mouse movement, and page click count during the visit',
          'Sends session data (time on page, behaviour signals) when the visitor leaves',
        ],
      },
      {
        heading: 'Verifying installation',
        content: 'After installing, visit your landing page with ?gclid=test appended to the URL. Then go to Click Log in your dashboard — you should see a new entry. Alternatively, use the Verify Installation button on your campaign page.',
      },
      {
        heading: 'Performance impact',
        content: 'The script loads asynchronously and has no impact on your page load speed or Core Web Vitals. It does not block page rendering.',
        note: 'The script only fires when a gclid (Google Ads click ID) or fbclid (Facebook) parameter is present in the URL. It does nothing on organic or direct visits.',
      },
    ],
  },

  'connecting-google-ads': {
    title: 'Connecting your Google Ads account',
    category: 'Getting Started',
    time: '4 min',
    intro: 'Connecting Google Ads allows ClickSaver to automatically push blocked IPs into your Google Ads campaign exclusion lists — no manual work required.',
    sections: [
      {
        heading: 'Step 1 — Go to Settings',
        content: 'Click Settings in the sidebar, then scroll to the Google Ads Integration section.',
      },
      {
        heading: 'Step 2 — Enter your Customer ID',
        content: 'Enter your Google Ads Customer ID (found in the top right of your Google Ads account, format: 123-456-7890). Enter it without dashes. Click Save.',
      },
      {
        heading: 'Step 3 — Connect via OAuth',
        content: 'Click Connect Google Ads. You will be redirected to Google to authorise ClickSaver. Select the Google account that has access to your Google Ads account and click Allow.',
        steps: [
          'The account you choose must have Admin or Standard access in Google Ads — Read-only and Email-only access will not work.',
          'If you manage multiple Google accounts, make sure you pick the one that is listed as a user on your Google Ads account (check at ads.google.com → Admin → Access and security).',
          'If the connection fails with a permission error, this is almost always because the selected account has insufficient access level in Google Ads.',
        ],
        note: 'ClickSaver only requests the minimum permissions needed to add IP exclusions. We cannot see your payment details, modify bids, or create ads.',
      },
      {
        heading: 'Step 4 — Confirm connection',
        content: 'After authorising, you will be redirected back to Settings. The status will show Google Ads connected in green.',
      },
      {
        heading: 'Step 5 — Link your campaigns',
        content: 'Go to Google Ads Sync in the sidebar. For each ClickSaver campaign, select the corresponding Google Ads campaign from the dropdown and click Link. Blocked IPs will now be automatically synced.',
      },
    ],
  },

  'understanding-fraud-report': {
    title: 'Understanding your first fraud report',
    category: 'Getting Started',
    time: '6 min',
    intro: 'Your fraud report shows you who is clicking your ads, which clicks are suspicious, and how much money ClickSaver has saved you. Here is how to read it.',
    sections: [
      {
        heading: 'Dashboard overview cards',
        content: 'The top row of your dashboard shows four key metrics:',
        steps: [
          'Total Clicks — all clicks recorded by ClickSaver this period',
          'Fraudulent Clicks — clicks that triggered fraud detection rules',
          'Blocked IPs — unique IPs currently blocked from seeing your ads',
          'Estimated Savings — money saved based on your CPC × blocked clicks',
        ],
      },
      {
        heading: 'Fraud score explained',
        content: 'Each click receives a fraud score from 0–100. Scores above your threshold trigger blocking. The score is based on: click frequency from the same IP, VPN/proxy usage, datacenter traffic, disabled JavaScript, behavioural signals, and geographic anomalies.',
      },
      {
        heading: 'Click log',
        content: 'The Click Log shows every individual click with: timestamp, IP address, country, device, fraud score, and block status. Click any row to see the full breakdown of why a click was flagged.',
      },
      {
        heading: 'Blocked IPs list',
        content: 'The Blocked IPs page shows all currently blocked IPs. You can manually unblock any IP if you believe it was incorrectly flagged, and add it to your whitelist to prevent future blocking.',
      },
      {
        heading: 'If you see no data yet',
        content: 'Data only appears after Google Ads clicks hit your tracking script. Make sure: (1) the script is installed on the correct landing page, (2) your Google Ads campaign is active and sending traffic, (3) you are viewing the correct date range in the dashboard.',
      },
    ],
  },

  // ─── INSTALLATION GUIDES ───────────────────────────────────────────────────

  'wordpress-installation': {
    title: 'WordPress installation guide',
    category: 'Installation Guides',
    time: '4 min',
    intro: 'Install the ClickSaver tracking script on your WordPress site in under 5 minutes — no plugin required.',
    sections: [
      {
        heading: 'Method 1 — Using a plugin (recommended)',
        content: 'The easiest method is to use the Insert Headers and Footers plugin:',
        steps: [
          'In your WordPress admin, go to Plugins → Add New',
          'Search for "Insert Headers and Footers" and install it',
          'Go to Settings → Insert Headers and Footers',
          'Paste your ClickSaver script into the Scripts in Header box',
          'Click Save',
        ],
      },
      {
        heading: 'Method 2 — Edit your theme',
        content: 'If you prefer not to use a plugin:',
        steps: [
          'Go to Appearance → Theme Editor',
          'Select header.php from the file list on the right',
          'Find the closing </head> tag',
          'Paste your ClickSaver script just before it',
          'Click Update File',
          'Warning: theme updates may overwrite this change. Use a child theme to be safe.',
        ],
      },
      {
        heading: 'Method 3 — Using Google Tag Manager',
        content: 'If you use GTM on your WordPress site, see the Google Tag Manager setup guide instead — it is the most flexible approach.',
      },
      {
        heading: 'Verifying installation',
        content: 'Visit your landing page and check your browser\'s developer tools (F12 → Network tab). Search for "clicksaver" — you should see a request to the tracking endpoint. Alternatively, check your Click Log in the dashboard for a new entry.',
      },
    ],
  },

  'shopify-installation': {
    title: 'Shopify installation guide',
    category: 'Installation Guides',
    time: '3 min',
    intro: 'Add ClickSaver to your Shopify store to protect your Google Ads campaigns from click fraud.',
    sections: [
      {
        heading: 'Step 1 — Access your theme code',
        content: 'In your Shopify admin, go to Online Store → Themes → click the three dots next to your active theme → Edit code.',
      },
      {
        heading: 'Step 2 — Edit theme.liquid',
        content: 'In the Layout folder on the left, click theme.liquid. Find the closing </head> tag (use Ctrl+F to search). Paste your ClickSaver script just before it.',
        code: `<!-- ClickSaver Tracking -->
<script async src="https://clicksaver.app/api/clicks/track.js?cid=YOUR_CAMPAIGN_ID"></script>`,
      },
      {
        heading: 'Step 3 — Save',
        content: 'Click Save in the top right. The script will now load on every page of your Shopify store.',
        note: 'If your Google Ads send traffic to a specific product page or landing page, the script will fire correctly as long as it is in theme.liquid.',
      },
      {
        heading: 'Step 4 — Verify',
        content: 'Click a Google Ads link to your store (or simulate one by adding ?gclid=test to your URL). Check your ClickSaver Click Log for the recorded visit.',
      },
    ],
  },

  'webflow-installation': {
    title: 'Webflow installation guide',
    category: 'Installation Guides',
    time: '3 min',
    intro: 'Install ClickSaver on your Webflow site using the built-in Custom Code settings.',
    sections: [
      {
        heading: 'Step 1 — Open site settings',
        content: 'In the Webflow Designer, click the Site Settings icon (gear icon) in the left toolbar, or go to your Webflow dashboard → Project Settings.',
      },
      {
        heading: 'Step 2 — Add custom code',
        content: 'Go to the Custom Code tab. In the Head Code section, paste your ClickSaver tracking script. Click Save Changes.',
      },
      {
        heading: 'Step 3 — Publish',
        content: 'Click Publish in the top right of the Designer to push the changes to your live site.',
        note: 'Webflow requires a paid plan to add custom code. If you are on the free plan, upgrade to at least the Basic plan.',
      },
      {
        heading: 'Step 4 — Verify',
        content: 'Visit your published Webflow site and check the browser console (F12) for any ClickSaver network requests. Then check your Click Log in the ClickSaver dashboard.',
      },
    ],
  },

  'google-tag-manager-setup': {
    title: 'Google Tag Manager setup',
    category: 'Installation Guides',
    time: '5 min',
    intro: 'If you use Google Tag Manager (GTM), you can install ClickSaver through GTM without touching your website code.',
    sections: [
      {
        heading: 'Step 1 — Create a new tag',
        content: 'In Google Tag Manager, go to Tags → New. Click Tag Configuration and select Custom HTML.',
      },
      {
        heading: 'Step 2 — Paste your script',
        content: 'Paste your ClickSaver tracking script into the HTML field.',
        code: `<script>
  (function(w,d,s,c){
    w._cs=w._cs||[];
    var f=d.getElementsByTagName(s)[0];
    var j=d.createElement(s);
    j.async=true;
    j.src='https://clicksaver.app/api/clicks/track.js?cid='+c;
    f.parentNode.insertBefore(j,f);
  })(window,document,'script','YOUR_CAMPAIGN_ID');
</script>`,
      },
      {
        heading: 'Step 3 — Set the trigger',
        content: 'Click Triggering and select All Pages (or a specific page trigger if your Google Ads only send traffic to certain pages). Click Save.',
      },
      {
        heading: 'Step 4 — Preview and publish',
        content: 'Click Preview to test the tag. Visit your website in the GTM preview mode and confirm the ClickSaver tag fires on the correct pages. Then click Submit and Publish.',
      },
      {
        heading: 'Tip: URL parameter trigger',
        content: 'For better performance, create a trigger that only fires when gclid is present in the URL. This means ClickSaver only loads for Google Ads visitors:',
        steps: [
          'Create a new Trigger → Trigger Type: Page View',
          'Select Some Page Views',
          'Condition: Page URL contains gclid',
          'Use this trigger on the ClickSaver tag',
        ],
      },
    ],
  },

  'custom-html-installation': {
    title: 'Custom HTML installation',
    category: 'Installation Guides',
    time: '2 min',
    intro: 'If your website is built with plain HTML or a framework not listed, here is how to install ClickSaver manually.',
    sections: [
      {
        heading: 'Step 1 — Find your HTML file',
        content: 'Open the HTML file of the page your Google Ads send traffic to. This is typically index.html or a dedicated landing page file.',
      },
      {
        heading: 'Step 2 — Paste the script',
        content: 'Paste your ClickSaver tracking script just before the closing </head> tag.',
        code: `<!DOCTYPE html>
<html>
<head>
  <title>My Landing Page</title>
  <!-- ... other head tags ... -->

  <!-- ClickSaver Tracking Script -->
  <script async src="https://clicksaver.app/api/clicks/track.js?cid=YOUR_CAMPAIGN_ID"></script>
</head>
<body>
  <!-- your page content -->
</body>
</html>`,
      },
      {
        heading: 'Step 3 — Deploy',
        content: 'Upload the updated HTML file to your server. The script will now fire for every visitor who arrives with a Google Ads gclid parameter.',
      },
    ],
  },

  // ─── GOOGLE ADS INTEGRATION ─────────────────────────────────────────────────

  'finding-customer-id': {
    title: 'Finding your Google Ads Customer ID',
    category: 'Google Ads Integration',
    time: '2 min',
    intro: 'Your Google Ads Customer ID is a 10-digit number that uniquely identifies your Google Ads account. ClickSaver needs it to push IP exclusions on your behalf.',
    sections: [
      {
        heading: 'Where to find it',
        content: 'Log in to Google Ads at ads.google.com. Your Customer ID is displayed in the top right corner of the screen, below your email address. It looks like: 123-456-7890.',
      },
      {
        heading: 'Entering it in ClickSaver',
        content: 'Go to Settings → Google Ads Integration. Enter your Customer ID in the field provided — enter it without dashes (e.g. 1234567890). Click Save.',
        note: 'If you manage multiple Google Ads accounts under an MCC (Manager Account), enter the MCC Customer ID, not the individual account ID. ClickSaver will automatically find the correct child account.',
      },
      {
        heading: 'MCC accounts',
        content: 'If you are an agency managing multiple client accounts, enter your MCC (manager) Customer ID. ClickSaver will list all accessible child accounts when you link campaigns.',
      },
    ],
  },

  'how-ip-exclusions-work': {
    title: 'How IP exclusions work in Google Ads',
    category: 'Google Ads Integration',
    time: '5 min',
    intro: 'IP exclusions tell Google Ads not to show your ads to specific IP addresses. ClickSaver automates this process so fraudulent IPs are excluded without any manual work.',
    sections: [
      {
        heading: 'What is a Google Ads IP exclusion?',
        content: 'Google Ads lets you block specific IP addresses from seeing your ads entirely. When an excluded IP searches on Google, your ad will not appear — protecting your budget from repeat fraudulent clicks.',
      },
      {
        heading: 'Account-level exclusions',
        content: 'ClickSaver uses account-level IP exclusions (not campaign-level). This means a blocked IP is excluded across all campaigns in your Google Ads account automatically — including Performance Max campaigns. You do not need to link individual campaigns for the block to take effect.',
        note: 'Account-level exclusions are more powerful than campaign-level ones because they protect all current and future campaigns simultaneously.',
      },
      {
        heading: 'How ClickSaver syncs exclusions',
        content: 'When ClickSaver detects a fraudulent IP, it:',
        steps: [
          'Records the IP in your Blocked IPs list immediately',
          'Pushes it to Google Ads automatically (real-time) if auto-sync is enabled',
          'Or queues it for the next scheduled sync (hourly, daily, weekly, or monthly)',
          'You can also trigger an immediate sync from the Google Ads Sync page at any time',
        ],
      },
      {
        heading: 'Google Ads limits',
        content: 'Google Ads allows up to 500 IP exclusions per account. ClickSaver enforces this cap automatically — you can configure the maximum in Detection Rules → Advanced → Exclusion List Max. Consider using CIDR notation (e.g. 1.2.3.0/24) to block entire subnets with a single entry.',
      },
    ],
  },

  'campaign-level-exclusions': {
    title: 'Setting up Google Ads sync',
    category: 'Google Ads Integration',
    time: '4 min',
    intro: 'ClickSaver syncs blocked IPs to your Google Ads account automatically. Here is how to connect and configure the sync.',
    sections: [
      {
        heading: 'Step 1 — Connect your Google Ads account',
        content: 'Go to Settings → Google Ads Integration. Enter your Google Ads Customer ID and click Connect Google Ads. Authorise ClickSaver via the Google OAuth screen.',
      },
      {
        heading: 'Step 2 — Link a Google Ads campaign (optional)',
        content: 'Go to Google Ads Sync in the sidebar. You can optionally link each ClickSaver campaign to a specific Google Ads campaign. This is used for reporting and the auto-sync button — but IP exclusions are applied at the account level, so all campaigns are protected regardless.',
      },
      {
        heading: 'Step 3 — Choose a sync schedule',
        content: 'In the Google Ads Sync page, select your preferred auto-sync interval: Hourly, Daily, Weekly, or Monthly. Hourly is recommended for active campaigns with significant traffic.',
      },
      {
        heading: 'Step 4 — Run initial sync',
        content: 'Click Sync Now to immediately push all currently blocked IPs to your Google Ads account. ClickSaver will confirm how many IPs were synced and report any errors.',
        note: 'It can take up to 30 minutes for newly added exclusions to take full effect across Google\'s ad serving network.',
      },
    ],
  },

  'verifying-blocks-working': {
    title: 'Verifying blocks are working',
    category: 'Google Ads Integration',
    time: '3 min',
    intro: 'After setting up ClickSaver, here is how to confirm that blocked IPs are actually being excluded from your Google Ads account.',
    sections: [
      {
        heading: 'Check the Blocked IPs list',
        content: 'Go to Blocked IPs in the sidebar. You should see a list of IPs that have been blocked. Each entry shows the IP, country, fraud signals, date blocked, and expiry date.',
      },
      {
        heading: 'Check the Google Ads Sync page',
        content: 'Go to Google Ads Sync → click Sync Now. The response shows how many IPs were synced and flags any errors. A successful sync confirms the IPs have been pushed to your Google Ads account.',
      },
      {
        heading: 'Verify in Google Ads directly',
        content: 'In Google Ads: click the Tools icon (wrench) → Shared Library → Exclusion lists. Select your account-level IP exclusion list. You should see the IPs ClickSaver pushed listed there.',
        note: 'Account-level IP exclusions are under Shared Library, not under individual campaign settings.',
      },
      {
        heading: 'Test with your own IP',
        content: 'You can test the full flow by temporarily adding your own IP to the blocked list (Blocked IPs → Add Manual Block), syncing, then checking Shared Library → Exclusion lists in Google Ads. Remember to remove yourself after testing.',
        note: 'Google Ads IP exclusions can take up to 1 hour to fully propagate across their network.',
      },
    ],
  },

  // ─── ANALYTICS & REPORTS ───────────────────────────────────────────────────

  'understanding-fraud-score': {
    title: 'Understanding your fraud score',
    category: 'Analytics & Reports',
    time: '5 min',
    intro: 'Every click tracked by ClickSaver receives a fraud score from 0 to 100. This score determines whether a click is flagged and the IP blocked.',
    sections: [
      {
        heading: 'Score ranges',
        content: 'Fraud scores fall into three bands:',
        steps: [
          '0–29 — Clean: normal traffic, no action taken',
          '30–69 — Suspicious: monitored but not blocked (unless monitoring only mode is off)',
          '70–100 — Fraudulent: IP is blocked and added to Google Ads exclusion list',
        ],
      },
      {
        heading: 'What affects the score',
        content: 'The fraud score is calculated from over 40 signals, including:',
        steps: [
          'Click frequency — how many times the same IP has clicked your ads',
          'VPN/proxy usage — is the IP routing through an anonymising service',
          'Datacenter IP — does the IP belong to a cloud or hosting provider',
          'JavaScript disabled — bots often disable JavaScript',
          'Time on site — did the visitor spend any time on the page',
          'Geographic anomaly — does the IP country match the browser language',
          'Device fingerprint — repeated visits from the same device',
        ],
      },
      {
        heading: 'Adjusting the threshold',
        content: 'By default, IPs with a score above 70 are blocked. You can lower this threshold (more aggressive) or raise it (more conservative) in Detection Rules → Sensitivity Thresholds.',
      },
    ],
  },

  'reading-analytics-dashboard': {
    title: 'Reading the fraud analytics dashboard',
    category: 'Analytics & Reports',
    time: '6 min',
    intro: 'The Fraud Analytics dashboard gives you a complete picture of click fraud activity across all your campaigns.',
    sections: [
      {
        heading: 'Date range selector',
        content: 'Use the date picker at the top right to filter data by Last 7 days, Last 30 days, or a custom range. All charts and tables update to reflect the selected period.',
      },
      {
        heading: 'Click volume chart',
        content: 'The main chart shows total clicks vs fraudulent clicks over time. Spikes in the fraud line may indicate a coordinated attack. Hover over any point to see the exact numbers for that day.',
      },
      {
        heading: 'Fraud breakdown table',
        content: 'Below the chart, you will see a breakdown by fraud type: VPN traffic, datacenter IPs, repeated clickers, behaviour anomalies, etc. This helps you understand what kind of fraud is targeting your campaigns.',
      },
      {
        heading: 'Top threat IPs',
        content: 'The Top Threats section shows the IPs responsible for the most fraudulent clicks. Click any IP to see its full click history and fraud score breakdown.',
      },
      {
        heading: 'Estimated savings',
        content: 'The savings calculation is: (number of blocked clicks) × (your average CPC). Your CPC is pulled from your linked Google Ads campaign. You can manually set the CPC from the Google Ads Sync page if needed.',
      },
    ],
  },

  'setting-up-email-reports': {
    title: 'Setting up email reports',
    category: 'Analytics & Reports',
    time: '3 min',
    intro: 'ClickSaver can send you automated weekly and monthly fraud protection reports by email, as well as instant alerts when a fraudulent IP is blocked.',
    sections: [
      {
        heading: 'Enable email reports',
        content: 'Go to Notifications in the sidebar. Toggle on Weekly Report and/or Monthly Report. Your registered account email is added automatically — you can also add up to 5 additional email addresses.',
      },
      {
        heading: 'IP block alerts',
        content: 'Enable the Google Ads toggle in Notifications to receive an email every time ClickSaver detects and blocks a fraudulent IP. Each alert includes the IP address, fraud score, detection signals, and timestamp.',
      },
      {
        heading: 'Report schedule',
        content: 'Weekly reports are sent every Monday at 8am UTC. Monthly reports are sent on the 1st of each month at 8am UTC.',
      },
      {
        heading: 'What is in the report',
        content: 'Each report includes:',
        steps: [
          'Total clicks and fraud rate for the period',
          'Number of IPs blocked',
          'Estimated ad spend saved',
          'Campaign-by-campaign breakdown with fraud rates',
        ],
      },
    ],
  },

  'exporting-click-data': {
    title: 'Exporting your click data',
    category: 'Analytics & Reports',
    time: '2 min',
    intro: 'You can export your click data as a CSV file for analysis in Excel, Google Sheets, or your own BI tools.',
    sections: [
      {
        heading: 'Export from Click Log',
        content: 'Go to Click Log in the sidebar. Apply any filters you want (date range, campaign, fraud status). Click the Export CSV button in the top right. The CSV will download with all visible columns.',
      },
      {
        heading: 'Export from Blocked IPs',
        content: 'Go to Blocked IPs → click Export CSV to download a list of all currently blocked IP addresses.',
      },
      {
        heading: 'CSV columns',
        content: 'The click export includes: timestamp, IP address, country, city, ISP, device type, browser, fraud score, fraud reasons, campaign, and block status.',
        note: 'Exports are limited to 10,000 rows per download. For larger exports, filter by date range and export in batches.',
      },
    ],
  },

  // ─── DETECTION RULES ────────────────────────────────────────────────────────

  'fraud-detection-signals': {
    title: 'How fraud detection signals work',
    category: 'Detection Rules',
    time: '7 min',
    intro: 'Every click that hits your Google Ads campaign is analysed by ClickSaver in real time. ClickSaver checks over 20 signals grouped into four categories — frequency, IP reputation, browser behaviour, and on-site behaviour — and combines them into a fraud score from 0 to 100. IPs that cross your configured threshold are automatically added to your Google Ads exclusion list.',
    sections: [
      {
        heading: 'How the fraud score works',
        content: 'Each signal detected adds points to the fraud score. A score of 0 means the click looks completely clean. A score of 100 means every available signal fired. The default block threshold is 70 — any IP scoring 70 or above is blocked. You can raise this threshold to reduce false positives or lower it to catch borderline cases. The score is recalculated on every click from the same IP, so a borderline IP can tip over the threshold on a repeat visit.',
        note: 'The fraud score is shown for every click in the Click Log and on each blocked IP in the Blocked IPs page. Use it to understand why a specific IP was or was not blocked.',
      },
      {
        heading: 'Signal group 1 — Click frequency (Block Reason: "Over threshold")',
        content: 'ClickSaver counts how many times the same IP address has clicked your ads across multiple time windows. You can configure up to 5 custom rules in Detection Rules → Click Thresholds. The defaults are:',
        steps: [
          '3 clicks within 10 minutes — covers rapid repeated clicking',
          '4 clicks within 7 days — catches slow-burn fraud that stays under hourly limits',
          '7 clicks within 30 days — catches persistent low-volume bots',
        ],
        note: 'These rules produce the "Over threshold" tag in your reports. Adjust the numbers in Detection Rules → Click Thresholds to match your typical legitimate visitor behaviour. A site with long sales cycles (e.g. B2B) may have real users clicking multiple times before converting — raise the thresholds accordingly.',
      },
      {
        heading: 'Signal group 2 — IP reputation (Block Reason: "VPN Software")',
        content: 'ClickSaver queries the IPQS database in real time to check the reputation of every IP. This check covers:',
        steps: [
          'VPN connections — commercial VPN services such as NordVPN, ExpressVPN, Surfshark, and hundreds of others',
          'Proxy servers — open proxies and anonymising proxy networks',
          'Tor exit nodes — the last relay in the Tor anonymity network before traffic reaches your site',
          'Datacenter IPs — IP ranges owned by AWS, Google Cloud, Azure, DigitalOcean, and other hosting providers. Legitimate users browse from home or office ISPs, not server farms.',
          'Known fraud networks — IPs flagged by multiple commercial fraud databases for prior abuse',
        ],
        note: 'VPN, Tor, and datacenter detection requires an IPQS API key configured in your backend (IPQS_API_KEY). Without this key, only click-frequency rules and browser signals apply. You can enable or disable each category individually in Detection Rules → Detection.',
      },
      {
        heading: 'Signal group 3 — Browser signals (Block Reason: "Bot-like Behaviour")',
        content: 'These signals are detected at the moment of the click, before the visitor even reaches your landing page:',
        steps: [
          'JavaScript disabled — the ClickSaver tracking script cannot execute. Virtually all real browsers have JavaScript enabled by default; bots and scrapers often do not. Controlled in Detection Rules → Detection → Block JavaScript Disabled Browsers.',
          'Headless browser — the user agent string indicates Chrome headless, PhantomJS, or Puppeteer — tools used to automate clicks at scale.',
          'Suspicious or missing user agent — the browser does not send a standard user agent string, or sends one consistent with automated tools.',
          'Repeated device fingerprint — the same device ID appears across multiple IP addresses, indicating the attacker is rotating proxies while using the same machine.',
        ],
        note: 'Browser signals are fast and cheap — they fire before the visitor spends any time on your site. Enable "Block JavaScript Disabled Browsers" in Detection Rules → Detection to catch the most common bot pattern.',
      },
      {
        heading: 'Signal group 4 — On-site behaviour (Block Reasons: "Bounced Immediately", "Bot-like Behaviour")',
        content: 'These signals are collected by the ClickSaver tracking script during the visitor\'s session and sent to ClickSaver when the visitor leaves. They require the tracking script to be installed and Behavior Analysis to be enabled in Detection Rules → Detection.',
        steps: [
          'Time on site (Bounced Immediately) — the visitor left in fewer seconds than your configured minimum. The default is 5 seconds. A real person reading your landing page almost always stays longer. Set the threshold in Detection Rules → Click Thresholds → Minimum Seconds On Site.',
          'No scroll detected — the visitor did not scroll the page at all. Combined with short time on site, this is a strong bot indicator.',
          'No mouse movement — no cursor movement was recorded during a desktop session. Human visitors always move their mouse.',
          'No page interactions — the visitor clicked nothing on the page — no buttons, links, forms, or CTAs.',
          'Bot-like behaviour (combined signal) — when three or more of the above on-site signals fire together (no scroll + no mouse + no interactions), ClickSaver raises the "Bot-like Behaviour" flag even if the time on site was above the minimum.',
        ],
        note: 'On-site behaviour signals are the most accurate because they measure what the visitor actually did — not just where they came from. A VPN user could be a real customer; a VPN user who also bounced immediately with no mouse movement is almost certainly not.',
      },
      {
        heading: 'Where to see signals in the dashboard',
        content: 'Every click record shows the signals that fired:',
        steps: [
          'Click Log — open any click row to see its fraud score and all triggered signals listed as colour-coded tags',
          'Blocked IPs — each blocked IP shows the reason it was blocked (the highest-scoring signal)',
          'Block Reason Breakdown chart — on the Analytics page, shows which signal categories are responsible for most of your blocks. The three most common are: Bounced Immediately, Bot-like Behaviour, and VPN Software.',
          'Test Rules — in Detection Rules, enter any IP address to see exactly which signals would fire and what score it would receive under your current settings',
        ],
      },
      {
        heading: 'Which Detection Rules setting controls which signal?',
        content: 'Use this as a quick reference:',
        steps: [
          '"Bounced Immediately" tag → Detection Rules → Click Thresholds → Minimum Seconds On Site (requires Behavior Analysis enabled)',
          '"Bot-like Behaviour" tag → Detection Rules → Detection → Block JavaScript Disabled Browsers and/or Behavior Analysis',
          '"VPN Software" tag → Detection Rules → Detection → VPN Blocking → Block VPN connections',
          '"Over threshold (10 min / 7 days / 30 days)" tags → Detection Rules → Click Thresholds → Click Fraud Threshold rules',
          '"Country Blocked" tag → Detection Rules → Country Blocking',
          '"Manually Excluded" tag → Detection Rules → IP Lists → Manually Exclude IPs',
        ],
        note: 'Adjusting any rule only affects new clicks going forward. Previously blocked IPs remain in your Google Ads exclusion list until their block duration expires or you manually unblock them.',
      },
    ],
  },

  'testing-detection-rules': {
    title: 'Testing your detection rules',
    category: 'Detection Rules',
    time: '4 min',
    intro: 'Before relying on ClickSaver to protect your ad spend, it is worth verifying that your detection rules are working correctly. There are three ways to test: the built-in IP tester, a real ad click via the Ad Preview Tool, or a manual test URL using your own domain.',
    sections: [
      {
        heading: 'Method 1 — Test any IP address instantly (no click needed)',
        content: 'Go to Detection Rules → scroll to the bottom → Test Rules panel. Enter any IP address and click Test. ClickSaver will show you:',
        steps: [
          'The fraud score (0–100) that IP would receive under your current saved rules',
          'The action — BLOCK or ALLOW',
          'The country and whether it is a VPN, proxy, or datacenter IP',
          'Every signal that fired, shown as tags (e.g. VPN Software, Country Blocked)',
        ],
        note: 'This test is completely safe — nothing is recorded in your Click Log and no IPs are blocked. Use it to check a suspicious IP you spotted in your logs, or to verify a rule is working after saving changes.',
      },
      {
        heading: 'Method 1 example — testing with a VPN',
        content: 'A quick way to verify VPN detection is working:',
        steps: [
          '1. Connect to any VPN (e.g. Proton VPN, NordVPN, ExpressVPN)',
          '2. Find your current VPN IP — visit whatismyip.com while connected',
          '3. Enter that IP in the Test Rules panel and click Test',
          '4. You should see: Proxy/VPN: Yes, Datacenter: Yes, fraud score near 100, action: BLOCK',
          '5. If VPN is not detected, your IPQS API key may not be configured — contact support',
        ],
      },
      {
        heading: 'Method 2 — Trigger a real click via Google Ad Preview Tool',
        content: 'This method generates a real tracked click with a genuine gclid parameter, exactly like a real visitor clicking your ad:',
        steps: [
          '1. Go to ads.google.com and sign in',
          '2. Click Tools (top right) → Planning → Ad Preview and Diagnosis',
          '3. Set Location to the country your campaign targets and Language to English',
          '4. Search for your ad keyword',
          '5. Your ad will appear — click it while your VPN is connected',
          '6. ClickSaver records the click with your VPN IP — check the Click Log within seconds',
        ],
        note: 'The Ad Preview and Diagnosis tool generates real gclid parameters, so ClickSaver will fully track the click including time on site and behaviour signals once the tracking script fires.',
      },
      {
        heading: 'Method 3 — Manual test URL (fastest method)',
        content: 'You can force ClickSaver to track any visit by appending a fake gclid to your domain URL. ClickSaver detects the gclid parameter and records the click regardless of whether it came from a real Google Ad.',
        steps: [
          '1. Take your campaign domain — e.g. dipacciusa.com',
          '2. Visit: https://dipacciusa.com/?gclid=test123 (replace the domain with yours)',
          '3. ClickSaver will log the visit from your current IP with a full fraud score',
          '4. Check Detection Rules → Test Rules or the Click Log to see the result',
        ],
        note: 'Replace dipacciusa.com with your own campaign domain. The gclid value (test123) can be anything — ClickSaver only checks that the parameter exists. This method also records on-site behaviour (time on site, scroll, mouse movement) if your tracking script is installed.',
      },
      {
        heading: 'What to check after a test click',
        content: 'After triggering a test click using any method above, go to:',
        steps: [
          'Click Log — your test click should appear within seconds. Open it to see the fraud score, country, all triggered signals, and whether it was blocked or allowed.',
          'Blocked IPs — if the score was above your threshold, the IP should appear here automatically.',
          'Google Ads exclusion list — if you have Google Ads sync enabled, go to Admin → Account settings → IP exclusions in Google Ads to confirm the IP was pushed there.',
        ],
      },
      {
        heading: 'My test click is not appearing in the Click Log',
        content: 'If the click is not showing up, check the following:',
        steps: [
          'Tracking script not installed — the ClickSaver script must be in the <head> of your landing page. Go to Setup → Website Setup to get your script and verify it is installed.',
          'Wrong campaign domain — the domain in your ClickSaver campaign must exactly match the domain you visited. Check for www vs non-www mismatches.',
          'No gclid parameter — if you visited the page without a gclid, ClickSaver will not record it. Use the ?gclid=test123 method to force tracking.',
          'Script blocked by browser extension — ad blockers can block the tracking script. Test in a browser with no extensions, or in an Incognito window.',
        ],
      },
    ],
  },

  'custom-detection-rules': {
    title: 'Creating custom detection rules',
    category: 'Detection Rules',
    time: '8 min',
    intro: 'ClickSaver\'s default rules work well for most advertisers, but you can create custom rules to match your specific traffic patterns.',
    sections: [
      {
        heading: 'Opening Detection Rules',
        content: 'Go to Detection → Detection Rules in the sidebar. You will see your current active rules and the option to add new ones.',
      },
      {
        heading: 'Threshold rules',
        content: 'Threshold rules block IPs that click more than X times within a time period. To add one:',
        steps: [
          'Click Add Rule → Threshold Rule',
          'Set the click limit (e.g. 5 clicks)',
          'Set the time window (e.g. 10 minutes, 1 day, 7 days)',
          'Click Save Rule',
        ],
      },
      {
        heading: 'Country blocking rules',
        content: 'If your ads only target certain countries, you can block all traffic from other countries:',
        steps: [
          'Go to Detection Rules → Country Blocking',
          'Choose Block mode (block listed countries) or Allow mode (only allow listed countries)',
          'Add the countries to your list',
          'Click Save',
        ],
      },
      {
        heading: 'Manual IP rules',
        content: 'You can manually add specific IPs or IP ranges to always block:',
        steps: [
          'Go to Detection Rules → Manual Exclusions',
          'Enter an IP address (e.g. 1.2.3.4) or CIDR range (e.g. 1.2.3.0/24)',
          'Click Add',
        ],
      },
      {
        heading: 'Testing your rules',
        content: 'After saving new rules, use the Click Log to monitor whether the rules are catching the right traffic. Check that legitimate users are not being blocked by reviewing the Blocked IPs list.',
      },
    ],
  },

  'adjusting-thresholds': {
    title: 'Adjusting click frequency thresholds',
    category: 'Detection Rules',
    time: '4 min',
    intro: 'Click frequency thresholds are rules that block an IP once it has clicked your ads more than a set number of times within a given time window. They are the backbone of ClickSaver\'s fraud detection and are fully customisable per campaign.',
    sections: [
      {
        heading: 'What is a threshold rule?',
        content: 'A threshold rule says: "if the same IP clicks my ad more than X times within Y minutes/hours/days, flag it as fraudulent." You can have up to 5 rules active at the same time, covering different time windows. The default rules are:',
        steps: [
          '3 clicks within 10 minutes — catches rapid repeated clicking (the most common bot pattern)',
          '4 clicks within 7 days — catches slow-burn fraud that deliberately stays under hourly limits',
          '7 clicks within 30 days — catches persistent low-volume bots operating over a month',
        ],
        note: 'When an IP triggers a threshold rule, the block reason tag in your reports will show "Over threshold (10 min)", "Over threshold (7 days)", etc. depending on which rule fired first.',
      },
      {
        heading: 'Adding and editing rules',
        content: 'Go to Detection Rules → Click Thresholds. Each rule has three fields:',
        steps: [
          'Clicks — the maximum number of clicks allowed before the IP is flagged',
          'Time value — the number of time units in the window (e.g. 10)',
          'Time unit — minutes, hours, or days',
        ],
        note: 'Click Save Rules (at the bottom of the page) after making any changes. Rules are saved per campaign — each campaign can have its own set of thresholds.',
      },
      {
        heading: 'How to set the right thresholds for your campaign',
        content: 'The right thresholds depend on how your real customers behave:',
        steps: [
          'E-commerce or lead gen (short sales cycle) — users typically click once or twice. Keep defaults or lower them slightly.',
          'B2B or high-consideration purchases — users may research over days or weeks, clicking the ad multiple times before converting. Raise the 7-day and 30-day limits to avoid false positives (e.g. 8 clicks / 7 days).',
          'Brand campaigns — your own brand name is unlikely to attract click fraud. You can set looser thresholds here.',
          'Competitor keywords — high fraud risk. Consider lowering the 10-minute threshold to 2 clicks.',
        ],
      },
      {
        heading: 'Minimum Seconds On Site',
        content: 'The Minimum Seconds On Site setting works alongside threshold rules. Any IP that lands on your site and leaves in fewer than the configured seconds is flagged as "Bounced Immediately". The default is 5 seconds — a real user reading even a single sentence stays longer than this. You can raise this value (e.g. to 10 seconds) for more aggressive bounce detection, or lower it if you have a very fast single-action landing page (e.g. a click-to-call page where the user calls immediately).',
        note: 'Bounced Immediately detection requires the ClickSaver tracking script to be installed and Behavior Analysis to be enabled in Detection Rules → Detection.',
      },
      {
        heading: 'Advanced Mode — per-campaign thresholds',
        content: 'Enable Advanced Mode (toggle at the top of the Click Thresholds panel) to configure separate rules for individual Google Ads campaigns within the same ClickSaver campaign. This is useful for accounts running both brand and non-brand campaigns, where you want different sensitivity for each.',
      },
    ],
  },

  'country-blocking': {
    title: 'Blocking clicks by country',
    category: 'Detection Rules',
    time: '4 min',
    intro: 'Country blocking lets you either block clicks from specific countries or restrict your campaign to only allow clicks from a list of approved countries. It is one of the most effective ways to eliminate low-quality traffic from regions you do not serve.',
    sections: [
      {
        heading: 'When to use country blocking',
        content: 'Country blocking is useful when:',
        steps: [
          'Your business only operates in specific countries — block everywhere else to eliminate irrelevant clicks entirely',
          'You are seeing a high volume of fraud from a specific country in your Click Log or Block Reason Breakdown',
          'Your Google Ads geographic targeting is already set to specific countries — mirror that restriction in ClickSaver for an extra layer of protection',
          'You run campaigns in English-speaking markets and are receiving clicks from regions where English is not widely spoken',
        ],
      },
      {
        heading: 'Block mode vs Allow mode',
        content: 'ClickSaver offers two modes in Detection Rules → Country Blocking:',
        steps: [
          'Block mode — clicks from the listed countries are blocked. All other countries are allowed. Use this when you only want to exclude a few problem regions.',
          'Allow mode — only clicks from the listed countries are allowed. All other countries are blocked. Use this when your campaign targets a known set of countries and you want to be strict about it.',
        ],
        note: 'Allow mode is the more aggressive option. If you run international campaigns or have customers from many countries, use Block mode with a short exclusion list instead.',
      },
      {
        heading: 'How to set up country blocking',
        content: 'Go to Detection Rules → Country Blocking:',
        steps: [
          '1. Choose Block mode or Allow mode using the toggle at the top',
          '2. Type a country name in the search box and select it from the dropdown',
          '3. Add as many countries as needed — each appears as a removable tag',
          '4. Click Save Rules at the bottom of the page',
        ],
        note: 'Country detection uses the IP geolocation database. It is accurate for most IPs, but a small number of VPN or datacenter IPs may report incorrect locations. For maximum accuracy, combine country blocking with VPN blocking.',
      },
      {
        heading: 'Checking where your clicks are coming from',
        content: 'Before setting up country blocking, review your Click Log to see which countries are generating suspicious traffic. Each click record shows the detected country code. You can also check your Google Ads geographic report to see where your paid clicks are coming from and compare it against what ClickSaver is detecting.',
      },
      {
        heading: 'Country blocking and the block reason tag',
        content: 'When an IP is blocked because of country rules, the block reason shown in your reports will be either "Country Blocked" (blocked in Block mode) or "Country Not Allowed" (excluded in Allow mode). These tags appear in the Click Log, Blocked IPs list, and the Block Reason Breakdown chart.',
      },
    ],
  },

  'monitoring-only-mode': {
    title: 'Using Monitoring Only mode',
    category: 'Detection Rules',
    time: '3 min',
    intro: 'Monitoring Only mode lets ClickSaver record and score every click without actually blocking any IP addresses. It is the safest way to evaluate ClickSaver\'s detection on your real traffic before enabling full blocking.',
    sections: [
      {
        heading: 'What Monitoring Only mode does',
        content: 'When Monitoring Only mode is enabled:',
        steps: [
          'Every click is still recorded in the Click Log',
          'Every click still receives a fraud score based on your configured rules',
          'Fraud signals are still detected and shown in reports',
          'IPs are NOT added to your Google Ads exclusion list',
          'No clicks are blocked — all traffic reaches your landing page',
        ],
        note: 'Think of Monitoring Only mode as a "shadow mode" — ClickSaver watches everything and shows you what it would block, without actually blocking anything.',
      },
      {
        heading: 'When to use Monitoring Only mode',
        content: 'There are two main use cases:',
        steps: [
          'New setup — when you first install ClickSaver, run in Monitoring Only for 1–2 weeks. This lets you see how your real traffic scores before enabling blocking. Check the Block Reason Breakdown and Click Log to confirm the detection looks accurate for your campaigns.',
          'Testing rule changes — if you are about to significantly tighten your thresholds (e.g. lowering the time-on-site minimum or reducing click limits), switch to Monitoring Only temporarily to preview what would be blocked under the new rules before committing.',
        ],
      },
      {
        heading: 'How to enable or disable it',
        content: 'Go to Detection Rules → Advanced → Monitoring Only Mode. Toggle it on to enable, off to resume blocking. Remember to click Save Rules at the bottom of the page.',
        note: 'Monitoring Only mode is per-campaign. You can run one campaign in monitoring mode while another is actively blocking.',
      },
      {
        heading: 'Switching from Monitoring Only to full blocking',
        content: 'When you are ready to enable blocking:',
        steps: [
          '1. Review your Click Log and confirm the fraud detections look accurate',
          '2. Go to Detection Rules → Advanced → Monitoring Only Mode and turn it off',
          '3. Click Save Rules',
          '4. ClickSaver will immediately start pushing blocked IPs to your Google Ads exclusion list on the next sync',
        ],
        note: 'IPs that were detected as fraudulent during the monitoring period are not retroactively blocked. Only clicks that occur after you disable Monitoring Only mode will be actively blocked.',
      },
    ],
  },

  'whitelisting-ips': {
    title: 'Whitelisting trusted IPs',
    category: 'Detection Rules',
    time: '3 min',
    intro: 'Whitelisting ensures that specific IP addresses are never blocked, even if they trigger fraud detection signals. Use this for your office, team members, or known legitimate users.',
    sections: [
      {
        heading: 'When to whitelist',
        content: 'Common use cases for whitelisting:',
        steps: [
          'Your office IP — prevent your own team\'s testing from being blocked',
          'Agency IPs — your PPC agency may review landing pages frequently',
          'Client IPs — if a client reviews their own ads regularly',
          'Known partner IPs — affiliates or resellers with high click volumes',
        ],
      },
      {
        heading: 'How to whitelist an IP',
        content: 'Go to Detection Rules → Whitelist. Enter the IP address (e.g. 1.2.3.4) or IP range (e.g. 1.2.3.0/24) and click Add. Whitelisted IPs will never be blocked, regardless of their fraud score.',
      },
      {
        heading: 'Finding your IP address',
        content: 'To find your current IP address, visit whatismyip.com. For a corporate office, ask your IT team for the static outbound IP range.',
      },
      {
        heading: 'Whitelist converted IPs',
        content: 'Enable Whitelist Converted IPs in Detection Rules to automatically whitelist any IP address that has completed a conversion (form submission, purchase, etc.) on your site. This prevents customers from being blocked if they revisit from the same IP.',
      },
    ],
  },

  // ─── BILLING & ACCOUNT ──────────────────────────────────────────────────────

  'changing-plan': {
    title: 'Upgrading or downgrading your plan',
    category: 'Billing & Account',
    time: '2 min',
    intro: 'You can change your ClickSaver plan at any time. Upgrades take effect immediately. Downgrades take effect at the end of your current billing period.',
    sections: [
      {
        heading: 'How to change your plan',
        content: 'Go to Settings → Billing & Domains → Subscription Details. Click Upgrade Plan or contact support at support@clicksaver.app to discuss your options.',
      },
      {
        heading: 'Upgrading',
        content: 'When you upgrade, the new plan takes effect immediately. You will be charged a prorated amount for the remainder of the current billing period, then the full new plan price on your next billing date.',
      },
      {
        heading: 'Downgrading',
        content: 'When you downgrade, you keep your current plan features until the end of the billing period. The lower plan takes effect on your next renewal date. No refund is issued for unused days.',
        note: 'If you downgrade and your current blocked IP count exceeds the new plan\'s limit, older blocks will be archived but not deleted.',
      },
      {
        heading: 'Annual billing discount',
        content: 'Switch to annual billing to save 20% compared to monthly billing. Go to Settings → Billing → Switch to Annual. The discount is applied immediately.',
      },
    ],
  },

  'updating-payment': {
    title: 'Updating your payment method',
    category: 'Billing & Account',
    time: '2 min',
    intro: 'You can update your credit card or payment details at any time through the Stripe billing portal.',
    sections: [
      {
        heading: 'How to update',
        content: 'Go to Settings → Billing & Domains → Payment Details → click Update Credit Card. You will be redirected to the Stripe billing portal where you can securely update your card details.',
      },
      {
        heading: 'Accepted payment methods',
        content: 'ClickSaver accepts all major credit and debit cards: Visa, Mastercard, American Express, and Discover. Payment processing is handled securely by Stripe.',
      },
      {
        heading: 'Failed payments',
        content: 'If a payment fails, you will receive an email notification at your account email address. ClickSaver will retry the payment 3 times over 7 days. If payment cannot be collected, your account will be suspended. Update your payment method as soon as possible to avoid interruption.',
        note: 'Your fraud protection data is retained for 30 days after suspension. Reactivate your subscription within this window to restore full access.',
      },
    ],
  },

  'understanding-invoice': {
    title: 'Understanding your invoice',
    category: 'Billing & Account',
    time: '3 min',
    intro: 'ClickSaver invoices are generated automatically at the start of each billing period and sent to your account email.',
    sections: [
      {
        heading: 'Where to find invoices',
        content: 'Go to Settings → Billing & Domains → Invoice History. All past invoices are listed here. Click View Invoice to see the full details, or Download to get a PDF.',
      },
      {
        heading: 'What is on your invoice',
        content: 'Each invoice shows:',
        steps: [
          'Invoice number and date',
          'Billing period (from date → to date)',
          'Plan name and price',
          'Any discounts or credits applied',
          'Tax (if applicable)',
          'Total charged',
          'Payment method used',
        ],
      },
      {
        heading: 'Adding billing details',
        content: 'To add your company name, VAT ID, or address to invoices, go to Settings → Billing & Domains → Business Information. Fill in your details and click Update Billing. These will appear on all future invoices.',
      },
      {
        heading: 'VAT and tax',
        content: 'If you are a business in Australia or the EU, tax may be applied based on your location. Enter your VAT/ABN in the Business Information section to have it shown on invoices for tax purposes.',
      },
    ],
  },

  'cancelling-subscription': {
    title: 'Cancelling your subscription',
    category: 'Billing & Account',
    time: '2 min',
    intro: 'You can cancel your ClickSaver subscription at any time. There are no cancellation fees.',
    sections: [
      {
        heading: 'How to cancel',
        content: 'Go to Settings → Billing & Domains → Cancel ClickSaver Account → click Cancel Account. Confirm the cancellation when prompted.',
      },
      {
        heading: 'What happens after cancellation',
        content: 'Your subscription will remain active until the end of the current billing period. You will not be charged again. After expiry:',
        steps: [
          'Fraud detection stops — the tracking script becomes inactive',
          'Google Ads sync stops — no new IPs will be pushed',
          'Your data is retained for 30 days',
          'After 30 days, all data is permanently deleted',
        ],
      },
      {
        heading: 'Reactivating',
        content: 'If you cancel and change your mind, go to Settings → Billing → Reactivate Subscription before the end of your billing period to restore your subscription without losing data.',
      },
      {
        heading: 'Before you cancel',
        content: 'If you are cancelling due to a problem, please contact support@clicksaver.app — we may be able to resolve the issue or offer a discount to help.',
      },
    ],
  },
};

// Helper: find article by slug
export function getArticle(slug) {
  return ARTICLES[slug] || null;
}

// Helper: find which section an article belongs to
export function getArticleSection(slug) {
  for (const section of SECTIONS) {
    const match = section.articles.find(a => a.slug === slug);
    if (match) return { section, article: match };
  }
  return null;
}

// Helper: get all articles flat
export function getAllArticles() {
  return SECTIONS.flatMap(s =>
    s.articles.map(a => ({ ...a, category: s.title, categoryColor: s.color }))
  );
}
